import type { StandingsGroup } from "../lib/api";
import { matchParticipantShortName } from "../lib/api";
import { dropMatchLabel, dropMatchState, nameWithDropMark } from "../lib/dropMatch";
import { SCHEDULE_PENDING_LABEL } from "../lib/matchScheduleStatus";
import { isTieMatch, jornadaNumber, playerWhen, scoreFromSide } from "../lib/playerDrawMine";
import { playerPairKey, sortedGroupParticipants, type PlayerDrawGroup } from "../lib/playerDrawSections";
import { rrNoScoreLabel } from "../lib/rrMatrix";

/**
 * Tabla de cruces del Draw del jugador con el look del póster (mismas clases
 * `rr-poster` de la Tabla visual del admin, #540) — de solo lectura: sin
 * botones, sin jornada seleccionada. En celular la columna de nombres se
 * queda fija al deslizar (`.pdraw-cross`). G-P / Games / Lugar salen de la
 * tabla que el jugador ya ve; sin ella, «—».
 */
export default function PlayerRrPosterTable({
  group,
  myParticipantId,
  standings,
  qualifyCount,
}: {
  group: PlayerDrawGroup;
  myParticipantId: number | null;
  standings: StandingsGroup | null;
  qualifyCount: number | null;
}) {
  const participants = sortedGroupParticipants(group);
  const rowsById = new Map((standings?.standings ?? []).map((row) => [row.participant_id, row]));
  const anyPlayed = !!standings && standings.standings.some((row) => row.matches_played > 0);
  return (
    <div className="pdraw-cross__wrap">
      <table className="round-robin-matrix rr-poster pdraw-cross" aria-label={`Tabla de cruces · ${group.name}`}>
        <thead>
          <tr>
            <th className="rr-poster__th-player" scope="col">
              {group.name}
            </th>
            {participants.map((participant, index) => (
              <th key={participant.id} scope="col" title={participant.display_name ?? undefined}>
                <span className={`rr-poster__num${participant.id === myParticipantId ? " is-me" : ""}`}>{index + 1}</span>
              </th>
            ))}
            <th className="rr-poster__th-stat" scope="col">G-P</th>
            <th className="rr-poster__th-stat" scope="col">Games</th>
            <th className="rr-poster__th-stat" scope="col">Lugar</th>
          </tr>
        </thead>
        <tbody>
          {participants.map((rowParticipant, rowIndex) => {
            const isMe = rowParticipant.id === myParticipantId;
            const standing = rowsById.get(rowParticipant.id) ?? null;
            const qualifies = !!standing && anyPlayed && qualifyCount != null && standing.position <= qualifyCount;
            return (
              <tr key={rowParticipant.id} className={isMe ? "is-me" : undefined}>
                <th scope="row" className="rr-poster__player">
                  <span className="round-robin-matrix__rank rr-poster__num">{rowIndex + 1}</span>
                  <span className="rr-poster__who">
                    <span className="round-robin-matrix__participant-name" title={rowParticipant.display_name ?? undefined}>
                      {nameWithDropMark(matchParticipantShortName(rowParticipant), rowParticipant)}
                      {isMe ? <span className="sr-only"> (tú)</span> : null}
                    </span>
                    {isMe ? <span className="pdraw-tu">Tú</span> : null}
                  </span>
                </th>
                {participants.map((columnParticipant) => {
                  const isDiagonal = rowParticipant.id === columnParticipant.id;
                  const match = isDiagonal ? null : group.matchesByPair.get(playerPairKey(rowParticipant.id, columnParticipant.id)) ?? null;
                  const hasWinner = !!match && match.winner_participant_id != null;
                  const score = hasWinner && match ? scoreFromSide(match, rowParticipant.id) : null;
                  // Contra una baja la celda dice qué pasó (Eduardo 2026-10-05);
                  // una serie por equipos no lleva chip de baja.
                  const drop = match && !isTieMatch(match) && !score ? dropMatchState(match) : null;
                  const decided = hasWinner && !drop;
                  const rowWon = decided && match?.winner_participant_id === rowParticipant.id;
                  const rowLost = decided && !rowWon;
                  const isPending = !!match && !hasWinner && !drop && String(match.status) !== "cancelled";
                  const jornada = match ? jornadaNumber(match.round_label) : null;
                  const when = match && isPending ? playerWhen(match) : null;
                  const isMineCell = !!match && myParticipantId != null && (rowParticipant.id === myParticipantId || columnParticipant.id === myParticipantId);
                  return (
                    <td
                      key={columnParticipant.id}
                      className={`round-robin-matrix__cell${isDiagonal ? " is-diagonal" : ""}${match ? " has-match" : ""}${rowWon ? " is-won" : ""}${rowLost ? " is-lost" : ""}${isPending ? " is-pending" : ""}${drop || (match && !decided && !isPending) ? " is-off" : ""}${drop?.kind === "cancelled" ? " is-drop" : ""}${isMineCell ? " is-mine" : ""}`}
                      data-drop-match={drop?.kind}
                      title={match?.round_label ?? undefined}
                    >
                      {isDiagonal ? (
                        <span aria-label="Mismo jugador" className="rr-poster__ball">
                          <svg viewBox="0 0 28 28" aria-hidden="true">
                            <circle cx="14" cy="14" r="12" fill="#d8f14e" stroke="#8aa61a" strokeWidth="2" />
                            <path d="M5 8c5 3 5 9 0 12M23 8c-5 3-5 9 0 12" fill="none" stroke="#ffffff" strokeWidth="2" />
                          </svg>
                        </span>
                      ) : match ? (
                        <div className="round-robin-matrix__match">
                          {decided ? (
                            <>
                              <strong className="pdraw-cross__verdict">{rowWon ? "Ganó" : "Perdió"}</strong>
                              <span className="round-robin-matrix__score">{score ?? rrNoScoreLabel(String(match.status))}</span>
                            </>
                          ) : drop ? (
                            <>
                              <span className="rr-poster__chip">{jornada != null ? `Jornada ${jornada}` : "Sin jornada"}</span>
                              <span className="round-robin-matrix__drop">{dropMatchLabel(drop)}</span>
                            </>
                          ) : isPending ? (
                            <>
                              <span className="rr-poster__chip">{jornada != null ? `Jornada ${jornada}` : "Sin jornada"}</span>
                              {isTieMatch(match) ? <strong>Serie</strong> : null}
                              {when?.pending ? (
                                <span className="pdraw-cross__when">{SCHEDULE_PENDING_LABEL}</span>
                              ) : when?.day ? (
                                <>
                                  <strong className="rr-poster__day">{when.day}</strong>
                                  {when.time || when.court ? (
                                    <span>{[when.time, when.court.replace(/^Cancha /, "C")].filter(Boolean).join(" · ")}</span>
                                  ) : null}
                                </>
                              ) : (
                                <span>por programar</span>
                              )}
                            </>
                          ) : (
                            <span>Cancelado</span>
                          )}
                        </div>
                      ) : null}
                    </td>
                  );
                })}
                <td className="rr-poster__stat rr-poster__stat--gp">{standing ? `${standing.wins}-${standing.losses}` : "—"}</td>
                <td className="rr-poster__stat">{standing ? `${standing.games_won}-${standing.games_lost}` : "—"}</td>
                <td className={`rr-poster__stat rr-poster__place${qualifies ? " is-qualified" : ""}`}>
                  {standing && anyPlayed ? `${standing.position}º` : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
